"use client";

// ─────────────────────────────────────────────────────────────────────────
// "This tab already finished buying the $1 trial."
//
// WHY THIS EXISTS — the dead /checkout entry the purchase leaves behind.
//
// Every trial checkout is entered with a PUSH: hooks/use-trial-offer.ts calls
// redirectToBilling(), which is window.location.assign. Completing it is
// another push (Paddle's successUrl / the Payment Element's returnUrl both land
// on /checkout/return), and only THAT step is later replaced — return-client
// ends on window.location.replace(returnTo). So a buyer who has just paid is
// left with this history:
//
//     [ … , build canvas , /checkout?type=trial&… , build canvas ]
//                          ^^^^^^^^^^^^^^^^^^^^^^ still there
//
// One Back press — the ordinary way out of a flow — reloads that /checkout
// entry, which re-mints the intent. The gate refuses it (correctly: the trial
// is live) and the page rendered the refusal full-screen: "Trial not available"
// with a Go back link, shown to someone whose $1 had just been taken.
//
// Two things are wrong with re-minting, not one. On Paddle the mint has a side
// effect — createPaddleTrialCheckout creates a NEW transaction, and there is no
// reuse guard on that branch (checkout-intent-reuse covers the Stripe Payment
// Element only). In the window between paying and fulfilment landing, the gate
// still says "eligible", so a Back press there mints a second trial checkout
// for a trial the buyer already owns.
//
// So the fix is not to explain the refusal better — it is not to ask. This
// marker records that the flow completed, and /checkout replaces itself with
// the destination the moment it sees one: nothing is minted, nothing flashes,
// and the replace() takes the dead entry out of history so the NEXT Back press
// carries on past it instead of landing here again.
//
// sessionStorage, per-tab, like lib/billing/checkout-intent-stash.ts: it
// describes ONE tab's round-trip through checkout, which is exactly the scope
// of the history stack it is repairing. Other tabs (and other devices) are
// covered by the server-authoritative check in app/checkout/checkout-client.tsx.
//
// TRIAL ONLY, deliberately. The trial is once-per-account for life, so a stale
// marker can never block a purchase the user is entitled to make again. A
// top-up is repeatable — the same buyer may want the same pack twice in one
// session — so generalising this would turn a fix into a regression.
// ─────────────────────────────────────────────────────────────────────────

const KEY = "panda:trial-checkout-settled";

/**
 * How long the marker is honoured.
 *
 * DELIBERATELY SHORT, and shorter than lib/billing/checkout-intent-stash.ts's
 * hour. That module's stash is the ONLY thing that can reopen the modal it
 * describes, so it has to outlive card-hunting and 3DS detours. This one is a
 * pure optimisation: when it expires, /checkout simply does what it did before —
 * asks the server, is told `trial_active`, and forwards the buyer anyway. So the
 * expiry costs nothing and buys the bound below.
 *
 * What it bounds: sessionStorage is per TAB, not per USER, and nothing in the
 * app clears it on its own (see the sign-out hook below, which is the direct
 * fix). A window this short is what keeps a mis-scoped marker from outliving the
 * purchase it describes.
 */
const TTL_MS = 15 * 60 * 1000;

/** Record that this tab completed the trial checkout. Call only from a
 *  CONFIRMED success — never from a failure the buyer may want to retry. */
export function markTrialCheckoutSettled(): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now() }));
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — non-fatal: the
       server-side entitlement check on /checkout still covers this. */
  }
}

/**
 * True when this tab has already completed the trial checkout.
 *
 * Read-only on purpose — it must stay idempotent. window.location.replace() is
 * asynchronous, so a re-render or a StrictMode second effect pass can ask again
 * before the navigation commits; a consume-on-read would answer `false` the
 * second time and let the page mint the very request this exists to prevent.
 */
export function wasTrialCheckoutSettled(): boolean {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    const t = (JSON.parse(raw) as { t?: unknown }).t;
    if (typeof t !== "number" || Date.now() - t > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Drop the marker.
 *
 * Called from lib/auth/logout.ts, and that call is not optional. The marker says
 * "the person in this tab owns the trial" — sign out and that stops being true,
 * but sessionStorage survives the sign-out and the whole tab session. Without
 * this, signing in as a second, genuinely eligible account in the SAME tab would
 * have that account's trial checkout silently bounce to returnTo with nothing
 * said, for as long as the marker lived.
 */
export function clearTrialCheckoutSettled(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}
